import { useCallback, useState } from 'react';
import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell from '../../../components/layout/TeacherShell';
import { cn } from '../../../lib/cn';
import BookReader from '../../../components/reader/BookReader';

const CATEGORY_LABELS = {
  novel: 'Novel',
  article: 'Article',
  non_fiction: 'Non-Fiction',
  textbook: 'Textbook',
  reference: 'Reference',
  presentation: 'Presentation',
  poetry: 'Poetry',
  drama: 'Drama',
  other: 'Other',
};

export default function LibraryShow({ book, chapters, categories = [], active_courses = [], course_list_options = null, series = null }) {
  const { t } = useTranslation();

  // Deep-link: chapter to open from the URL (?chapter=ID).
  const [initialChapterId] = useState(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('chapter');
  });

  // Reflect the active chapter in the URL so refresh / back / return-via-nav
  // re-lands on the same chapter.
  const handleChapterLoaded = useCallback(({ chapter_id }) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('chapter', String(chapter_id));
      window.history.replaceState(null, '', url.toString());
    } catch {
      // Ignore — URL updating is a nice-to-have.
    }
  }, []);

  const scrollTitle = (
    <>
      <Link href="/teacher/library" className="hover:text-blue-600 dark:hover:text-blue-400">{t('teacherLibrary.title')}</Link>
      {'  ›  '}
      {book.author ? `${book.title}  –  ${book.author}` : book.title}
    </>
  );

  return (
    <TeacherShell maxWidth="wide" scrollTitle={scrollTitle}>
      <div className="mb-6">
        <Link
          href="/teacher/library"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-4 transition-colors"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('teacherLibrary.title')}
        </Link>

        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{book.title}</h1>
            {book.author && (
              <p className="mt-2 text-gray-500 dark:text-gray-400">by {book.author}</p>
            )}
            {series && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Part of{' '}
                <Link
                  href={`/teacher/book_series/${series.id}`}
                  className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {series.name}
                </Link>
                {series.position_in_series != null && (
                  <span className="text-gray-400 dark:text-gray-500">
                    {' '}&middot; Book {series.position_in_series}
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <ItemTypeBadge itemType={book.item_type} />
              {book.category && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {CATEGORY_LABELS[book.category] || book.category}
                </span>
              )}
              <Link
                href={`/teacher/library/${book.id}/edit`}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {t('common.edit')}
              </Link>
            </div>

            {book.item_type === 'link' && book.url && (
              <a
                href={book.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate max-w-md"
              >
                {book.url}
              </a>
            )}

            {active_courses.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <span className="text-xs text-gray-400 dark:text-gray-500">{t('teacherLibrary.usedIn')}</span>
                {active_courses.map(course => (
                  <Link
                    key={course.id}
                    href={`/teacher/courses/${course.id}`}
                    className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                  >
                    {course.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {series && series.siblings.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
              Other books in {series.name}
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {series.siblings.map((sibling) => (
                <Link
                  key={sibling.id}
                  href={`/teacher/library/${sibling.id}`}
                  className="shrink-0 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800 transition-colors"
                >
                  <div className="flex items-baseline gap-2">
                    {sibling.position_in_series != null && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                        #{sibling.position_in_series}
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1 max-w-[16rem]">
                      {sibling.title}
                    </span>
                  </div>
                  {sibling.author && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 max-w-[16rem]">
                      {sibling.author}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <BookReader
        book={book}
        chapters={chapters}
        courseListOptions={course_list_options}
        initialChapterId={initialChapterId}
        onChapterLoaded={handleChapterLoaded}
      />
    </TeacherShell>
  );
}

const TYPE_BADGE_STYLES = {
  epub: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  pdf: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  pptx: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  link: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
};

function ItemTypeBadge({ itemType }) {
  const label = (itemType || 'epub').toUpperCase();
  const style = TYPE_BADGE_STYLES[itemType] || TYPE_BADGE_STYLES.epub;

  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', style)}>
      {label}
    </span>
  );
}
